"use client";

import { asText } from "@prismicio/client";
import { useCallback, useEffect, useRef, useState } from "react";

import Heading from "../Heading";
import { CarouselControls } from "./CarouselControls";
import { TestimonialCard } from "./TestimonialCard";
import { useAnimatedHeight } from "./useAnimatedHeight";

import type { Testimonial2Item } from "./TestimonialCard";
import type { Content } from "@prismicio/client";

type Primary = Content.Testimonials2SliceDefaultPrimary;

const ITEMS_PER_PAGE = {
  carousel: 1,
  "side-by-side": 2,
} as const;

const SWIPE_THRESHOLD_PX = 50;

/*
  Safety net for dropping the outgoing page. `animationend` normally does it,
  but it never fires when the animation is disabled (reduced motion) or when
  the element is never painted (backgrounded tab). Comfortably longer than the
  0.6s cross-fade.
*/
const LEAVE_CLEANUP_MS = 800;

const DEFAULT_LABEL = "Testimonials";

function itemsPerPage(layout: Primary["layout"]) {
  return ITEMS_PER_PAGE[layout as keyof typeof ITEMS_PER_PAGE] ?? 1;
}

/**
 * Testimonial carousel. Shows one testimonial at a time, or two side by side,
 * and pages through them in a loop with manual previous/next controls. With a
 * single page's worth of testimonials it renders statically, without controls
 * or carousel semantics.
 */
export function Testimonials2({
  heading,
  items,
  layout,
}: {
  heading: Primary["heading"];
  items: Array<Testimonial2Item>;
  layout: Primary["layout"];
}) {
  const perPage = itemsPerPage(layout);
  const pageCount = Math.max(1, Math.ceil(items.length / perPage));
  const [page, setPage] = useState(0);
  const [hasTurned, setHasTurned] = useState(false);
  const [leavingPage, setLeavingPage] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const {
    endTurn,
    height,
    isTurning,
    ref: trackRef,
    startTurn,
  } = useAnimatedHeight<HTMLDivElement>();

  // `page` can outlive a shorter list when a preview swaps content underneath.
  const currentPage = Math.min(page, pageCount - 1);
  const isLooping = pageCount > 1;
  const isSingle = items.length === 1;
  const label = asText(heading) || DEFAULT_LABEL;

  const itemsForPage = (index: number) =>
    items.slice(index * perPage, index * perPage + perPage);

  /*
    The outgoing page stays mounted so it can cross-fade with the incoming one
    instead of leaving a gap. Keying by page index (rather than by role) means
    React keeps its DOM when it switches from current to leaving — otherwise a
    video testimonial would remount and reload its player mid-transition.
  */
  const renderedPages =
    leavingPage !== null && leavingPage !== currentPage
      ? [leavingPage, currentPage]
      : [currentPage];

  const turnPage = (delta: number) => {
    setHasTurned(true);
    setLeavingPage(currentPage);
    startTurn();
    setPage((((currentPage + delta) % pageCount) + pageCount) % pageCount);
  };

  /*
    Ends the turn and drops the outgoing page.

    `endTurn` cannot rely on the height transition alone: two pages can measure
    the same height (side-by-side pages holding the same mix of long and short
    testimonials), in which case no transition runs and `transitionend` never
    fires — which would leave the gradient mask applied permanently.
  */
  const finishTurn = useCallback(() => {
    setLeavingPage(null);
    endTurn();
  }, [endTurn]);

  useEffect(() => {
    if (leavingPage === null) {
      return;
    }

    const timer = setTimeout(finishTurn, LEAVE_CLEANUP_MS);

    return () => clearTimeout(timer);
  }, [finishTurn, leavingPage]);

  const handleTouchEnd = (endX: number) => {
    const startX = touchStartX.current;
    touchStartX.current = null;

    if (!isLooping || startX === null) {
      return;
    }

    const distance = endX - startX;

    if (Math.abs(distance) >= SWIPE_THRESHOLD_PX) {
      turnPage(distance < 0 ? 1 : -1);
    }
  };

  return (
    <section
      aria-label={isLooping ? label : undefined}
      aria-roledescription={isLooping ? "carousel" : undefined}
      className={`testimonials2 testimonials2--${layout ?? "carousel"}${
        isSingle ? " testimonials2--single" : ""
      }`}
      data-test-id="testimonials2"
    >
      <div className="testimonials2-header">
        <div className="rte section-heading testimonials2-heading">
          <Heading field={heading} />
        </div>
        {isLooping ? (
          <CarouselControls
            onNext={() => turnPage(1)}
            onPrevious={() => turnPage(-1)}
          />
        ) : null}
      </div>

      <div
        aria-live="polite"
        className={`testimonials2-viewport${isTurning ? " is-turning" : ""}`}
        onTouchEnd={(event) => handleTouchEnd(event.changedTouches[0].clientX)}
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTransitionEnd={(event) => {
          if (
            event.target === event.currentTarget &&
            event.propertyName === "height"
          ) {
            endTurn();
          }
        }}
        style={{ height }}
      >
        <div className="testimonials2-track" ref={trackRef}>
          {renderedPages.map((pageIndex) => {
            const isLeaving = pageIndex !== currentPage;

            return (
              <div
                aria-hidden={isLeaving || undefined}
                aria-label={
                  !isLeaving && isLooping
                    ? `${currentPage + 1} of ${pageCount}`
                    : undefined
                }
                aria-roledescription={
                  !isLeaving && isLooping ? "slide" : undefined
                }
                className={
                  isLeaving
                    ? "testimonials2-page testimonials2-page--leaving"
                    : `testimonials2-page${hasTurned ? " is-entering" : ""}`
                }
                data-test-id={
                  isLeaving
                    ? "testimonials2-page-leaving"
                    : "testimonials2-page"
                }
                inert={isLeaving || undefined}
                key={pageIndex}
                onAnimationEnd={
                  isLeaving
                    ? (event) => {
                        if (event.target === event.currentTarget) {
                          finishTurn();
                        }
                      }
                    : undefined
                }
                role={!isLeaving && isLooping ? "group" : undefined}
              >
                {itemsForPage(pageIndex).map((item, index) => (
                  <TestimonialCard
                    item={item}
                    key={pageIndex * perPage + index}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
